import { Routes } from "../../navigation";

import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";

import { useNavigate, useParams } from "react-router-dom";
import { CONTACT_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";
import ContactDetailCard from "./components/ContactDetailCard/ContactDetailCard";
import TabMenu from "../components/TabMenu/TabMenu";
import { ContactDetailPageCmp, ContactDetailPageProps } from "./types";
import { useCallback, useState } from "react";

import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { SIDEBAR_ITEM_CONTACT } from "app/components/Sidebar/consts";
import { deleteContactAPI, getContactAPI } from "services/api/contacts/api";
import { transformRoute, useTranslate } from "helpers";
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import Badge from "ui/components/Badge/Badge";
import ContactEditCard from "./components/ContactDetailCard/components/ContactEditCard/ContactEditCard";
import { Loader } from "ui/components/Loader";
import { useApiCall } from "services/api/hooks/useApiCall";


const ContactDetailPage: ContactDetailPageCmp = (
  props: ContactDetailPageProps
) => {
  const { id } = useParams();
  const { t } = useTranslate();
  const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const navigate = useNavigate();

  const { data: formValues, isLoading, refetch } = useApiCall(
    () => getContactAPI(id!),
    [id],
    { onNotFound: ContactRoutes.ContactSearchPageRouter }
  );

  const breadcrumbs: BreadcrumbItem[] = [
    { text: SIDEBAR_ITEM_CONTACT.text, route: SIDEBAR_ITEM_CONTACT.route },
  ];

  const handleDeleteConfirm = useCallback(async () => {
    if (!id) return;
    try {
      await deleteContactAPI(t, id);
      navigate(transformRoute(ContactRoutes.ContactSearchPageRouter, {}));
    } catch {
      // error handled by snackbar
    }
    setShowDeleteDialog(false);
  }, [id, t]);

  const handleEditSuccess = (contactId?: string) => {
    setShowEditModal(false);
    if (contactId && contactId !== id) {
      navigate(transformRoute(ContactRoutes.ContactDetailPageRouter, { id: contactId }));
    } else {
      refetch();
    }
  };

  if (!id) return <></>;

  if (isLoading || !formValues) return <Loader isLoading={true}>{null}</Loader>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <div className="flex justify-between">
              <div className="flex flex-col gap-2">
                <div className="flex gap-3">
                  {formValues.pictures && formValues.pictures.length > 0 && (
                    <img src={formValues.pictures[0].url} alt={formValues.companyName ?? ""} title={formValues.companyName ?? ""} className="h-12 w-12 object-contain rounded-full" />
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <PageTitle
                        title={`${t(CONTACT_DETAIL_PAGE_TITLE.text)} [${formValues.companyName || `${formValues.firstName} ${formValues.lastName}`}]`}
                        icon={CONTACT_DETAIL_PAGE_TITLE.icon}
                        iconColor={CONTACT_DETAIL_PAGE_TITLE.iconColor}
                      />
                      {formValues.contactType && (
                        <Badge
                          text={formValues.contactType.id === "C" ? "Tvrtka" : "Osoba"}
                          variant={formValues.contactType.id === "C" ? "info" : "success"}
                          height="small"
                          showDot={true}
                        />
                      )}
                    </div>
                    <Breadcrumb items={breadcrumbs} />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <Button
                  variant="primary"
                  startIcon="/media/icons/plus.svg"
                  children="Promjeni"
                  onClick={() => setShowEditModal(true)}
                />
                <Button
                  variant="remove"
                  startIcon="/media/icons/trash.svg"
                  children="Obriši"
                  onClick={() => setShowDeleteDialog(true)}
                />
                <Button
                  variant="secondary"
                  startIcon="/media/icons/setting.svg"
                  onClick={() => setShowAddWidgetModal(true)}
                />
              </div>
            </div>
          </PageHeader>
          <TabMenu contactId={id} contactTypeId={formValues?.contactType?.id} />
        </div>
        <div className="px-0 py-4 mx-[-1px] xl:pl-4">
          <ContactDetailCard
            contactDetail={formValues}
            showAddWidgetModal={showAddWidgetModal}
            setShowAddWidgetModal={setShowAddWidgetModal}
            onEditContact={() => setShowEditModal(true)} />
        </div>
      </div>

      <ContactEditCard
        contactId={id ?? null}
        show={showEditModal}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEditModal(false)}
      />
      <Dialog
        open={showDeleteDialog}
        title="Brisanje kontakta"
        message="Jeste li sigurni da želite obrisati ovaj kontakt? Ova radnja se ne može poništiti."
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

ContactDetailPage.route = Routes.ContactDetailPageRouter;

export default ContactDetailPage;
